<template>
  <div
    class="relative shrink-0 flex select-none text-shadow-none amp-block"
    :class="[
      getStyle(styles, 'container'),
      isFull ? '!w-auto !h-auto aspect-square' : ''
    ]"
    :style="{
      viewTransitionName: 'user-picture'
    }"
  >
    <template v-if="user">
      <component
        :is="getComponent()"
        :to="{ query: { ...route.query, user: displayedUser.username }}"
        class="flex items-center relative justify-center font-bold grow bg-cover bg-center border leading-none bg-charcoal-100  dark:bg-dark-900 text-charcoal-400"
        :style="{
          borderColor: theme?.base ? theme.base.background.base : 'transparent',
        }"
        :class="[
          theme?.base ? 'border-[3px]' : (image ? 'border-none' : `border-charcoal-200`),
          getStyle(styles, 'image'),
          isBlocked ? 'bg-ruby-200 !text-ruby-500' : '',
          imageClass,
          isAnonymous ? 'text-light-950 dark:text-white' : '',
          isBlur ? 'overflow-hidden' : '',
          borderClasses
        ]"
        draggable="false"
        @mouseenter="isHover = true"
        @mouseleave="isHover = false"
      >
        <img :src="image" class="absolute top-0 left-0 w-full h-full object-cover" :class="[ getStyle(styles, 'image'), isBlur ? 'blur scale-110' : '' ]" :style="{ transformStyle: 'preserve-3d', backfaceVisibility: 'hidden' }" loading="lazy" draggable="false" v-if="image && !isBlocked && !isAnonymous" />
        <IconBase name="cute/ghost-fill" :width="size === '4xs' || size === '3xs' ? 10 : (size === '2xs' || size === 'xs' ? 14 : (size === 'sm' || size === 'sm+' ? 18 : (size === 'lg' || size === 'xl' ? 26 : (size === '2xl' || size === '3xl' ? 32 : 20))))" v-else-if="isAnonymous" />
        <IconBase name="cute/user-fill" :width="size === '4xs' || size === '3xs' ? 10 : (size === '2xs' ? 12 : (size === 'xs' ? 14 : (size === 'sm' || size === 'sm+' ? 18 : (size === 'lg' || size === 'xl' ? 26 : (size === '2xl' || size === '3xl' ? 32 : 20)))))" v-else />
      </component>
      
      <div
        class="absolute rounded-full"
        :class="[
          theme?.base ? 'border-[3px]' : (image ? '' : `border-charcoal-200 dark:border-dark-900`),
          user.lastLogged === 0 ? 'bg-emerald-400' : 'bg-[#c0e4d1]',
          getStyle(styles, 'online'),
        ]"
        :style="theme?.base ? {
          borderColor: theme.base.background.base
        } : {}"
        v-if="(user.lastLogged <= 1) && isOnline && !isBlocked && !isAnonymous"
      />

      <ImageEmoji name="Yellow+Heart" class="!absolute z-10 pointer-events-none" size="sm" :class="[ getStyle(styles, 'ambassador') ]" v-if="isEvent && user.isAmbassador" />
      <ImageEmoji :name="capitalize(flag)" class="!absolute z-10 pointer-events-none" size="sm" :class="[ getStyle(styles, 'flag') ]" v-else-if="flag && !isHideBadges && !isBlocked" />

      <ImageEmoji name="Prohibited" class="!absolute z-10 pointer-events-none" :class="[ getStyle(styles, 'flag') ]" size="sm" v-if="isBlocked && !isHideBadges" />
      
      <slot />
    </template>
    <div class="flex items-center justify-center font-bold text-charcoal-400 rounded-full bg-charcoal-200" :class="[ getStyle(styles, 'container') ]" v-else>
      {{ name }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { defineStyles, getStyle } from '@/utils/useStyles'
import { IUser, Theme, User, UserFlags } from '@/classes'
import UserStore from '@/store/user'
import ImageEmoji from '../Image/ImageEmoji.vue'

const props = withDefaults(defineProps<{
  user: User | IUser | null
  size?: '4xs' | '3xs' | '2xs' | 'xs' | 'sm' | 'sm+' | 'base' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl'
  styleType?: 'round' | 'square'
  theme?: Theme | null
  borderClasses?: string
  isHideBadges?: boolean
  isBlur?: boolean
  isEvent?: boolean
  isAnonymous?: boolean
  isFull?: boolean
  imageClass?: string
  flag?: (typeof UserFlags[number]) | ''
  isTooltip?: boolean
  isOnline?: boolean
  isLink?: boolean
}>(), {
  size: 'base',
  styleType: 'round',
  borderClasses: '',
  isAnonymous: false,
  isBlur: false,
  isEvent: false,
  theme: null,
  isOnline: false,
  isTooltip: false,
  flag: '',
  isHideBadges: false,
  imageClass: '',
  isFull: false,
  isLink: false
})

const route = useRoute()
const userStore = UserStore()
const { currentUser, colorTheme } = storeToRefs(userStore)

const displayedUser = computed(() => props.user instanceof User ? props.user : new User(props.user))

const isBlocked = computed(() => (currentUser.value?.blocked ?? []).includes(displayedUser.value?._id))

const name = computed(() => {
  return props.user?.name ? props.user.name.slice(0, 1).toUpperCase() : 'A'
})

const flag = computed(() => {
  return props.flag ? props.flag : (displayedUser.value?.isSubscriber ? displayedUser.value?.mainFlag : null)
})

// PROFILE HOVER

const isHover = ref(false)
const getComponent = () => {
  
  return props.isTooltip || props.isLink ? resolveComponent('NuxtLink') : 'div'
}

const image = computed(() => {
  return displayedUser.value.image?.getSize(props.size == '4xl' || props.size == '3xl' || props.size == '2xl' || props.size == 'xl' || props.size === 'md' || props.size === 'lg' ? 'm' : 's')
})

const styles = computed(() => defineStyles({
  container: {
    size: {
      '4xs': 'text-4xs size-4',
      '3xs': 'text-4xs size-5',
      '2xs': 'text-3xs size-6',
      xs: 'text-2xs size-7',
      sm: 'size-9',
      'sm+': 'size-10',
      base: 'text-lg size-12',
      md: 'text-xl size-14',
      lg: 'text-2xl size-16',
      xl: 'text-3xl size-20',
      '2xl': 'text-4xl size-24',
      '3xl': 'text-4xl size-28',
      '4xl': 'text-4xl size-60',
    }
  },
  badge: {
    size: {
      '4xs': 'hidden',
      '3xs': 'hidden',
      '2xs': 'hidden',
      xs: 'hidden',
      sm: 'w-3 h-3',
      base: 'w-4 h-4',
      lg: 'w-5 h-5',
      xl: 'w-7 h-7',
      '2xl': 'w-6 h-6',
      '3xl': 'size-6',
      '4xl': 'w-7 h-7',
    }
  },
  flag: {
    size: {
      '3xs': 'w-2.5 -bottom-[1px] -right-[1px]',
      '2xs': 'w-2.5 -bottom-[2px] -right-[1px]',
      'xs': 'w-3 -bottom-[1px] -right-[1px]',
      'sm': 'w-3 -bottom-[0px] -right-[1px]',
      'sm+': 'w-4 -bottom-[1px] -right-[1px]',
      base: 'w-4 -bottom-[1px] -right-[1px]',
      md: 'w-5 -bottom-[1px] -right-[1px]',
      lg: 'w-5 -bottom-[1px] -right-[1px]',
      xl: 'w-7 -bottom-[4px] -right-[1px]',
      '2xl': 'w-8 -bottom-[4px] -right-[1px]',
      '3xl': 'w-9 -bottom-[4px] -right-[1px]',
      '4xl': 'w-16 -bottom-[4px] -right-[1px]',
    }
  },
  ambassador: {
    size: {
      '2xs': 'size-3 -bottom-[0] -right-[2px]',
      'xs': 'size-2 -bottom-[0px] -right-[1px]',
      'sm': 'size-3 -bottom-[0px] -right-[1px]',
      'sm+': 'size-4 -bottom-[1px] -right-[1px]',
      base: 'size-5 -bottom-[1px] -right-[1px]',
      md: 'size-5 -bottom-[1px] -right-[1px]',
      lg: 'size-5 -bottom-[1px] -right-[1px]',
      xl: 'size-7 -bottom-[4px] -right-[1px]',
      '2xl': 'size-8 -bottom-[4px] -right-[1px]',
      '3xl': 'size-9 -bottom-[4px] -right-[1px]',
      '4xl': 'size-16 -bottom-[4px] -right-[1px]',
    }
  },
  image: {
    styleType: {
      round: 'rounded-full',
      square: 'rounded-lg'
    }
  },
  online: {
    size: {
      '2xs': 'size-1.5 top-0 left-0',
      'xs': 'size-4 top-0.5 left-0.5',
      'sm': 'size-2 top-[0.025rem] left-[0.025rem]',
      'sm+': 'size-2.5 top-0 -left-0',
      base: 'size-3 top-0 left-0',
      md: 'size-3 top-0.5 left-0',
      lg: 'size-3.5 top-0.5 left-0.5',
      xl: 'size-4 top-1.5 left-1.5',
      '2xl': 'size-5 top-1.5 left-1.5',
      '3xl': 'size-5 top-1.5 left-1.5',
      '4xl': 'size-4 top-0.5 left-0.5',
    }
  }
}, props))

</script>